import { DataPoint } from './orthofoto.service';
import LineString from 'ol/geom/LineString';
import { Feature } from 'ol';
import { Style, Icon, Stroke, Text, Fill } from 'ol/style';
import { Point } from 'ol/geom';
import { InfluxOrthoDataPoint } from 'src/app/api/generated';

export function generateSVG(dx, dy, color, referenceScaleSize) {
  dx += referenceScaleSize / 2;
  dy += referenceScaleSize / 2;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" fill="none" pointer-events="none" ` +
    `width="${referenceScaleSize}" height="${referenceScaleSize}">
    <line pointer-events="none" x1="${referenceScaleSize / 2}" y1="${
    referenceScaleSize / 2
  }" y2="${dy}" x2="${dx}" stroke-width="2" stroke="${color}"></line></svg>`;
  return svg;
}

export function circleSVG(
  color: string = 'blue',
  width: number = 20,
  height: number = 20,
  radius: number = 4
) {
  return `<svg width="${width}" height="${height}" version="1.1" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="${radius}" fill="${color}"/>
    </svg>`;
}

export interface AxisConfig {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  text: string;
}

export interface DataFilterConfig {
  enabled: boolean;
  from: string;
  till: string;
}

export interface OrthofotoUserPreferences {
  selected_date: string;
}

export interface Datasource {
  id: string;
  name: string;
  checked: boolean;
}

export interface OrthofotoConfigGeneric {
  guid: string;
  type: string;
  datasources: Datasource[];
  headline: string;
  axis: Array<AxisConfig>;
  filter: DataFilterConfig;
  user_settings: OrthofotoUserPreferences;
  dynamic_center_point: {
    difference_hours: number;
    color_ok: string;
    color_warning: string;
  };
  color_height_vector: string;
  color_2d_vector: string;
  scale_size_factor: number;
}

export const ORTHOPHOTO_FIELDS = ['dx', 'dz', 'dy', 'x', 'y', 'd2d'];

// Parsing config is not required in future methods
export function parseConfig(config: any): OrthofotoConfigGeneric {
  const axis: Array<AxisConfig> = config.axis.map((element) => {
    return {
      x1: Number(element.x1),
      y1: Number(element.y1),
      x2: Number(element.x2),
      y2: Number(element.y2),
      text: element.text,
    };
  });
  return <OrthofotoConfigGeneric>{
    guid: config.guid,
    type: config.type,
    datasources: config.datasources,
    headline: config.headline,
    axis: axis,
    filter: {
      enabled: config.filter.enabled === 'true',
      from: config.filter.from,
      till: config.filter.till,
    },
    user_settings: {
      selected_date: config.user_settings?.selected_date,
    },
    dynamic_center_point: {
      difference_hours: config.dynamic_center_point.difference_hours,
      color_ok: config.dynamic_center_point.color_ok,
      color_warning: config.dynamic_center_point.color_warning,
    },
    color_height_vector: config.color_height_vector,
    color_2d_vector: config.color_2d_vector,
    scale_size_factor: config.scale_size_factor,
  };
}

export function largestValueInDataPoint(
  datapoint: InfluxOrthoDataPoint
): number {
  return Math.max(Math.abs(datapoint.dx), Math.abs(datapoint.lengthdXdY));
}

export function getBahnachseFeature(p1, p2, displayText) {
  const lineString = new LineString([p1, p2]);

  const lineStringFeature = new Feature({
    geometry: lineString,
  });

  lineStringFeature.setStyle(() => {
    const styles = [];
    lineString.forEachSegment((start, end) => {
      const dx = end[0] - start[0];
      const dy = end[1] - start[1];

      const rotation = Math.atan2(dy, dx);
      // arrows
      styles.push(
        new Style({
          geometry: new Point(end),
          image: new Icon({
            src: 'assets/orthophoto/arrow.svg',
            anchor: [0.75, 0.5],
            rotateWithView: true,
            rotation: -rotation,
          }),
        }),
        new Style({
          stroke: new Stroke({
            color: 'white',
            width: 2,
          }),
          text: new Text({
            text: displayText,
            rotateWithView: true,
            rotation: -rotation + Math.PI,
            stroke: new Stroke({
              color: 'black',
            }),
            fill: new Fill({
              color: 'white',
            }),

            scale: 1.3,
            overflow: false,
            offsetY: -10,
          }),
        })
      );
    });
    return styles;
  });
  return lineStringFeature;
}

export function getCustomControl(referenceScaleSize, translateService?: any): HTMLElement {
  const div = document.createElement('div');
  const movementHeight = translateService?.instant('app.movementHeight') || 'Bewegung Höhe';
  const movementPosition = translateService?.instant('app.displacementPosition') || 'Bewegung Lage';
  const scale = translateService?.instant('app.scale') || 'Maßstab';

  div.innerHTML = `<div id="scale-factor"><div>${scale}: <span id="scaleFactorValue"></span>m</div><svg width="${
    referenceScaleSize / 2
  }" height="5">
    <line pointer-events="none" x1="0" y1="0" y2="0" x2="${
      referenceScaleSize / 2
    }" stroke-width="5" stroke="red"></line></svg></div>

    <div id="scale-factor">${movementHeight} <svg width="5" height="20">
    <line pointer-events="none" x1="0" y1="0" y2="20" x2="0" stroke-width="5" stroke="yellow"></line></svg></div>

    <div id="scale-factor">${movementPosition} <svg width="20" height="10">
    <line pointer-events="none" x1="0" y1="0" y2="0" x2="20" stroke-width="5" stroke="red"></line></svg></div>
    `;
  div.style.width = `${referenceScaleSize / 2}px`;
  div.style.padding = '5px';
  div.style.marginLeft = '10px';
  div.style.marginTop = '10px';
  div.style.position = 'aboslute';
  div.style.bottom = '10px';
  div.style.fontFamily = 'sans-serif';
  div.className = 'ol-control ol-unselectable bottom-left';
  div.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
  return div;
}
